import { useState } from 'react';
import {
  PaperPlaneRightIcon,
  WarningCircleIcon,
  CheckCircleIcon,
  PencilIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ArrowUUpLeftIcon,
  WarningIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/utils/formatters';
import { format } from 'date-fns';
import { WorkbenchTransaction } from '../hooks/useWorkbench';

interface WorkbenchSectionProps {
  transactions: WorkbenchTransaction[];
  totalClaimAmount: number;
  failedCount: number;
  onRemove: (transactionIds: string | string[]) => void;
  onClear: () => void;
  onEditTransaction: (transaction: WorkbenchTransaction) => void;
  onSendAll: () => void;
  onClearFailureStatus?: (transactionIds?: string | string[]) => void;
  isSending?: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

export function WorkbenchSection({
  transactions,
  totalClaimAmount,
  failedCount,
  onRemove,
  onClear,
  onEditTransaction,
  onSendAll,
  onClearFailureStatus,
  isSending = false,
}: WorkbenchSectionProps) {
  const [isSendDialogOpen, setSendDialogOpen] = useState(false);
  const [isClearDialogOpen, setClearDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const handleSendAll = () => {
    // Clear failure statuses before retrying
    onClearFailureStatus?.();
    onSendAll();
    setSendDialogOpen(false);
  };

  const handleClear = () => {
    onClear();
    setClearDialogOpen(false);
  };

  const isEmpty = transactions.length === 0;
  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));
  const paginatedTransactions = transactions.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value));
    setCurrentPage(0); // Reset to first page when changing page size
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Workbench
                {transactions.length > 0 && (
                  <Badge variant="secondary">{transactions.length}</Badge>
                )}
                {failedCount > 0 && (
                  <Badge variant="destructive" className="ml-1">
                    {failedCount} failed
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Transactions staged for sending to suppliers
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {!isEmpty && (
                <>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">
                      Total Claim Amount
                    </div>
                    <div className="text-lg font-bold">
                      {formatCurrency(totalClaimAmount, {
                        locale: 'en-CA',
                        currency: 'CAD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClearDialogOpen(true)}
                    >
                      <ArrowUUpLeftIcon className="h-4 w-4 mr-2" />
                      Return All
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setSendDialogOpen(true)}
                      disabled={isSending}
                    >
                      {failedCount > 0 ? (
                        <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
                      ) : (
                        <PaperPlaneRightIcon className="h-4 w-4 mr-2" />
                      )}
                      {isSending
                        ? 'Sending...'
                        : failedCount > 0
                          ? 'Retry Send All'
                          : 'Send All'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isEmpty ? (
            <div className="p-6 text-center text-muted-foreground">
              <div className="text-lg mb-2">Workbench is empty</div>
              <div className="text-sm">
                Add transactions from the available list below to get started.
              </div>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>SO #</TableHead>
                    <TableHead>Ship Type</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Claim Amount</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTransactions.map((transaction) => (
                    <TableRow
                      key={transaction.id}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        transaction.failureReason
                          ? 'bg-destructive/5 hover:bg-destructive/10'
                          : ''
                      }`}
                      onClick={() => onEditTransaction(transaction)}
                    >
                      <TableCell className="w-[40px]">
                        {transaction.failureReason && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <WarningIcon className="h-4 w-4 text-destructive" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px]">
                                <p className="font-semibold">Send failed:</p>
                                <p>{transaction.failureReason}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {transaction.tradingPartner || 'N/A'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {transaction.fileThreeLetterDivision}
                        </div>
                      </TableCell>
                      <TableCell>{transaction.rexelSalesOrderNumber}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {transaction.shipType || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {transaction.orderDate
                          ? format(
                              new Date(transaction.orderDate),
                              'MM/dd/yyyy',
                            )
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {transaction.customerName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {transaction.customerShipToCity},{' '}
                          {transaction.customerShipToStateProvince}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium truncate max-w-[150px]">
                          {transaction.itemInternalCode}
                        </div>
                        <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                          {transaction.description}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {transaction.isModified && (
                            <Badge variant="secondary" className="text-xs">
                              Modified
                            </Badge>
                          )}
                          <span className="font-medium">
                            {formatCurrency(transaction.claimAmount, {
                              locale: 'en-CA',
                              currency: 'CAD',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditTransaction(transaction);
                            }}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Return to available transactions"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemove(transaction.id);
                            }}
                          >
                            <ArrowUUpLeftIcon className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Pagination Controls */}
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Rows per page:
                  </span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground ml-4">
                    Showing {currentPage * pageSize + 1}-
                    {Math.min(
                      (currentPage + 1) * pageSize,
                      transactions.length,
                    )}{' '}
                    of {transactions.length}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    <CaretLeftIcon className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={currentPage === totalPages - 1}
                  >
                    <CaretRightIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send All Confirmation Dialog */}
      <Dialog open={isSendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {failedCount > 0
                ? 'Retry Send All Transactions'
                : 'Send All Transactions'}
            </DialogTitle>
            <DialogDescription>
              {failedCount > 0 ? (
                <>
                  You have {failedCount} failed transaction(s). This will retry
                  sending all {transactions.length} transactions in the
                  workbench.
                </>
              ) : (
                <>
                  Are you sure you want to send all {transactions.length}{' '}
                  transactions in the workbench? This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2 text-yellow-600">
              <WarningCircleIcon className="h-5 w-5" />
              <p className="text-sm">
                Total claim amount:{' '}
                {formatCurrency(totalClaimAmount, {
                  locale: 'en-CA',
                  currency: 'CAD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendAll} disabled={isSending}>
              {failedCount > 0 ? (
                <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
              ) : (
                <CheckCircleIcon className="h-4 w-4 mr-2" />
              )}
              {isSending
                ? 'Sending...'
                : failedCount > 0
                  ? 'Retry Send All'
                  : 'Confirm Send All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog open={isClearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return All to Available</DialogTitle>
            <DialogDescription>
              Are you sure you want to return all {transactions.length}{' '}
              transactions to the available transactions list? You can add them
              back to the workbench later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleClear}>
              <ArrowUUpLeftIcon className="h-4 w-4 mr-2" />
              Return All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
